import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { api, OCCASIONS } from "@/lib/api";
import { toast } from "sonner";

export default function InquiryDialog({ open, onOpenChange, property, defaultValues = {} }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    location: defaultValues.location || "Hyderabad",
    check_in: defaultValues.check_in || "",
    check_out: defaultValues.check_out || "",
    guests: defaultValues.guests || 10,
    occasion: defaultValues.occasion || OCCASIONS[5],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        email: form.email || undefined,
        check_in: form.check_in || undefined,
        check_out: form.check_out || undefined,
        guests: Number(form.guests) || 1,
        property_id: property?.id,
      });
      toast.success("Inquiry sent — our host desk will reach out within 30 minutes.");
      onOpenChange(false);
      setForm((f) => ({ ...f, name: "", phone: "", email: "", message: "" }));
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#FAFAF8] border-[#0E2E24]/10 rounded-3xl" data-testid="inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-[#0E2E24]">
            {property ? `Enquire: ${property.name}` : "Plan your celebration"}
          </DialogTitle>
          <DialogDescription className="font-body text-[#111111]/70">
            Share a few details — our host desk will build a quote and call you within 30 minutes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Your name" required>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Aditi" data-testid="inquiry-name" />
            </Field>
            <Field label="Phone" required>
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 98…" data-testid="inquiry-phone" />
            </Field>
          </div>
          <Field label="Email (optional)">
            <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@brand.com" data-testid="inquiry-email" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check-in">
              <Input type="date" value={form.check_in} onChange={(e) => update("check_in", e.target.value)} data-testid="inquiry-check-in" />
            </Field>
            <Field label="Check-out">
              <Input type="date" value={form.check_out} onChange={(e) => update("check_out", e.target.value)} data-testid="inquiry-check-out" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Guests">
              <Input type="number" min={1} value={form.guests} onChange={(e) => update("guests", e.target.value)} data-testid="inquiry-guests" />
            </Field>
            <Field label="Occasion">
              <Select value={form.occasion} onValueChange={(v) => update("occasion", v)}>
                <SelectTrigger data-testid="inquiry-occasion"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Anything special?">
            <Textarea rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Surprise cake, DJ, decor, vendor prefs…" data-testid="inquiry-message" />
          </Field>

          <DialogFooter className="pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0E2E24] hover:bg-[#1F5A43] text-[#FAFAF8] rounded-full py-6 font-sora"
              data-testid="inquiry-submit"
            >
              {submitting ? "Sending…" : "Send inquiry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.18em] uppercase text-[#1F5A43] font-semibold mb-1.5 block">
        {label} {required && <span className="text-[#F4A930]">*</span>}
      </Label>
      {children}
    </div>
  );
}
